import { useState, useRef, useEffect, useMemo } from 'react';

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder: string;
  maxWidth?: string;
}

export function AutocompleteDropdown({ options, value, onChange, placeholder, maxWidth = '180px' }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        // Reset query to selected label when closing
        if (value) {
          const selected = options.find((o) => o.value === value);
          setQuery(selected?.label ?? value);
        } else {
          setQuery('');
        }
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [value, options]);

  // Sync query with external value changes
  useEffect(() => {
    if (value) {
      const selected = options.find((o) => o.value === value);
      setQuery(selected?.label ?? value);
    } else {
      setQuery('');
    }
  }, [value, options]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (opt: Option) => {
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(undefined);
    setQuery('');
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
    // If cleared, reset filter
    if (!e.target.value) {
      onChange(undefined);
    }
  };

  const handleFocus = () => {
    setOpen(true);
    // Select all text on focus for easy replacement
    inputRef.current?.select();
  };

  return (
    <div ref={ref} className="relative" style={{ maxWidth }}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="input w-full pr-7"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-spi-slate hover:text-red-600 text-sm"
          >
            &times;
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-spi-parchment rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filtered.slice(0, 50).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-spi-seed cursor-pointer ${
                opt.value === value ? 'bg-spi-green/10 text-spi-green-dark font-medium' : ''
              }`}
            >
              {opt.label}
            </button>
          ))}
          {filtered.length > 50 && (
            <div className="px-3 py-2 text-xs text-spi-slate text-center border-t border-spi-parchment">
              Type to narrow results...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
